/**
 * `/data-risk` — WP-L5, Task 53. `web/shapes/data-risk.md` is the agreed shape.
 *
 * Two halves of one question — *has company data left, or nearly left, and is anybody
 * already on it* — and a third section naming, field by field, what this build does
 * not publish. The third is part of the page for `AbsentSurfaces`' reason.
 *
 * ── Four reads, one per trigger, all server-filtered ────────────────────────
 *
 * `GET /alerts` takes **one** `trigger`; the router refuses a repeated parameter. So
 * four requests rather than one read filtered in the browser, because a client-side
 * filter over a cursor page answers "…among these fifty" and there is no total to
 * notice the error against. Each tile then counts only its own rows, which is a claim
 * the request supports.
 *
 * ── One read that cannot be server-filtered, and it says so ─────────────────
 *
 * `GET /activities/websites` publishes **no `blacklisted` parameter** — the flag is
 * on the row. So the blocked-domain table reads one page and filters it here, against
 * the rule above, and the caption prints *what it counted out of the page's own
 * total* rather than a number that would quietly mean "…among the first two
 * hundred". Closing that properly is a server-side filter, which is a finding rather
 * than something a client can fix.
 *
 * ── A region may be refused while the others load ───────────────────────────
 *
 * The reads have different permission requirements: the alert reads need
 * `resolve_incidents` / `view_department` / `view_workspace`, and the domain table
 * needs `view_websites`. A session can reach this route (whose menu gate is
 * `view_usb_events` / `view_file_events`) and be refused one region. That is rendered
 * **in place**, with the server's own sentence, never as an empty table — an empty
 * region and a refused one are the two facts this build has a standing rule about not
 * collapsing.
 */

import { useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { alertsPath, websiteUsagePath } from '../api/paths';
import type { AlertListResponse, WebsiteUsageListResponse } from '../api/types';
import { useApiQuery } from '../api/useApiQuery';
import { accessHeaders } from '../access/reason';
import { AccessReasonGate } from '../access/AccessReasonGate';
import { censusFor, DATA_RISK_TRIGGERS } from '../alerts/inbox';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { EmptyState } from '../states/EmptyState';
import { PartialData } from '../states/PartialData';
import { Skeleton } from '../states/Skeleton';
import { StatusNote } from '../states/StatusNote';
import { ScreenHeading } from '../shell/ScreenHeading';
import { AbsentSurfaces } from './datarisk/AbsentSurfaces';
import { TriggerTile } from './datarisk/TriggerTile';

const ALERT_LIMIT = 50;
const DOMAIN_LIMIT = 200;

/** The window the page asks about. Thirty days, printed on the page. */
const WINDOW_DAYS = 30;

function isoDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function DataRiskScreen(): ReactNode {
  const { t } = useI18n();
  const [reason, setReason] = useState('');

  const headers = accessHeaders(reason);
  const headerOption = headers === undefined ? {} : { headers };

  // Computed once per mount rather than per render: a date recomputed on every render
  // would change the path string identity and re-issue four sensitive-access reads —
  // each writing a `wp_data_access_logs` row — whenever anything on the page moved.
  const [range] = useState(() => {
    const now = Date.now();
    return { from: isoDate(now - WINDOW_DAYS * 86_400_000), to: isoDate(now) };
  });

  const usb = useApiQuery<AlertListResponse>(
    headers === undefined ? null : alertsPath({ limit: ALERT_LIMIT, trigger: 'usb_unapproved' }),
    0,
    headerOption,
  );
  const files = useApiQuery<AlertListResponse>(
    headers === undefined
      ? null
      : alertsPath({ limit: ALERT_LIMIT, trigger: 'bulk_file_operation' }),
    0,
    headerOption,
  );
  const sites = useApiQuery<AlertListResponse>(
    headers === undefined ? null : alertsPath({ limit: ALERT_LIMIT, trigger: 'blocked_website' }),
    0,
    headerOption,
  );
  const apps = useApiQuery<AlertListResponse>(
    headers === undefined ? null : alertsPath({ limit: ALERT_LIMIT, trigger: 'blocked_app' }),
    0,
    headerOption,
  );

  const domains = useApiQuery<WebsiteUsageListResponse>(
    headers === undefined
      ? null
      : websiteUsagePath({ from: range.from, to: range.to, limit: DOMAIN_LIMIT }),
    0,
    headerOption,
  );

  const reads = [usb, files, sites, apps];
  const census = useMemo(
    () =>
      DATA_RISK_TRIGGERS.map((trigger, index) =>
        censusFor(trigger, reads[index]?.data?.data ?? []),
      ),
    // `reads` is rebuilt on every render, so it is not a dependency; the four `data`
    // references are what actually change and they are named individually. There is
    // no `react-hooks` plugin in this repository's eslint config — this is a
    // hand-checked dependency list, and the check is that `censusFor` reads nothing
    // else.
    [usb.data, files.data, sites.data, apps.data],
  );

  const anyLoading = reads.some((read) => read.status === 'loading');
  const firstError = reads.find((read) => read.status === 'error' && read.error !== null);
  const anyMore = reads.some((read) => read.data?.page.has_more === true);

  const domainRows = (domains.data?.data ?? []).filter((row) => row.blacklisted === true);
  const domainPage = domains.data?.data.length ?? 0;
  const domainTotal = domains.data?.total ?? 0;

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('nav.dataRisk')}</ScreenHeading>
      <p className="wp-small wp-muted">{t('dataRisk.intro')}</p>

      <AccessReasonGate
        value={reason}
        subject={t('dataRisk.subjectWord')}
        testId="l5-datarisk-access"
        onOpen={setReason}
      />

      {reason === '' ? (
        <StatusNote severity="info" title={t('access.closed.title')} testId="l5-datarisk-closed">
          <p>{t('access.closed.body', { subject: t('dataRisk.subjectWord') })}</p>
        </StatusNote>
      ) : (
        <>
          <section className="wp-section" aria-labelledby="l5-tiles-heading" data-testid="l5-tiles">
            <h2 className="wp-section__title" id="l5-tiles-heading" tabIndex={-1}>
              {t('dataRisk.tiles.title')}
            </h2>
            <p className="wp-small wp-muted" data-testid="l5-tiles-counted">
              {t('dataRisk.tiles.counted')}
            </p>

            {anyLoading ? (
              <Skeleton
                rows={2}
                variant="block"
                label={t('dataRisk.subjectWord')}
                testId="l5-tiles-skeleton"
              />
            ) : null}

            {firstError?.error === undefined || firstError.error === null ? null : (
              <ApiErrorNote
                error={firstError.error}
                onRetry={firstError.reload}
                subject={t('dataRisk.subjectWord')}
                testId="l5-tiles-error"
              />
            )}

            {anyMore ? (
              <PartialData excludes={t('dataRisk.partial.excludes')} testId="l5-tiles-partial" />
            ) : null}

            {/* Four figures ruled off the page, not four cards. They are the same
                kind of reading taken four ways, and a border around each one would
                say they are four separate objects. */}
            <ul className="wp-c-figures">
              {census.map((entry) => (
                <TriggerTile key={entry.trigger} census={entry} />
              ))}
            </ul>
          </section>

          <section
            className="wp-section"
            aria-labelledby="l5-domains-heading"
            data-testid="l5-domains"
          >
            <h2 className="wp-section__title" id="l5-domains-heading">
              {t('dataRisk.domains.title')}
            </h2>
            <p className="wp-small wp-muted" data-testid="l5-domains-window">
              {t('dataRisk.domains.window', { from: range.from, to: range.to })}
            </p>

            {domains.status === 'loading' ? (
              <Skeleton
                rows={3}
                variant="block"
                label={t('dataRisk.domains.subject')}
                testId="l5-domains-skeleton"
              />
            ) : null}
            {domains.status === 'error' && domains.error !== null ? (
              // In place. A refused region is not an empty one, and the two are the
              // facts this build's standing rule forbids collapsing.
              <ApiErrorNote
                error={domains.error}
                onRetry={domains.reload}
                subject={t('dataRisk.domains.subject')}
                testId="l5-domains-error"
              />
            ) : null}

            {domains.data === null ? null : (
              <>
                <p className="wp-small wp-muted" data-testid="l5-domains-counted">
                  {t('dataRisk.domains.counted', {
                    matched: domainRows.length,
                    page: domainPage,
                    total: domainTotal,
                  })}
                </p>
                {domainRows.length === 0 ? (
                  <EmptyState
                    title={t('dataRisk.domains.empty.title')}
                    description={t('dataRisk.domains.empty.body')}
                    testId="l5-domains-empty"
                  />
                ) : (
                  <div className="wp-table-scroll">
                    <table className="wp-table" data-testid="l5-domains-table">
                      <thead>
                        <tr>
                          <th scope="col">{t('dataRisk.domains.col.domain')}</th>
                          {/* Luật Số Thẳng Cột: three columns a reader compares down
                              the page, so all three are right-aligned tabular
                              numerals — heads included, or the head sits over the
                              wrong edge of its column. */}
                          <th className="wp-num" scope="col">
                            {t('dataRisk.domains.col.seconds')}
                          </th>
                          <th className="wp-num" scope="col">
                            {t('dataRisk.domains.col.visits')}
                          </th>
                          <th className="wp-num" scope="col">
                            {t('dataRisk.domains.col.employees')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {domainRows.map((row) => (
                          <tr key={`${row.domain ?? 'withheld'}:${row.category}`}>
                            <td>
                              {/* `domain: null` is withheld under this caller's
                                  grants, not "no domain". Said, not blanked. */}
                              {row.domain ?? t('dataRisk.domains.withheld')}
                            </td>
                            <td className="wp-num">{row.total_seconds}</td>
                            <td className="wp-num">{row.visit_count}</td>
                            <td className="wp-num">
                              {typeof row.employee_count === 'number'
                                ? row.employee_count
                                : t('dataRisk.domains.noEmployeeCount')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </section>

          <AbsentSurfaces />
        </>
      )}
    </div>
  );
}
